import { clsx } from 'clsx'
import { useId, useRef, type ButtonHTMLAttributes, type KeyboardEvent, type ReactNode } from 'react'
import { currentLanguage, useLanguage } from '@/lib/i18n'
import { RollingNumber } from './count'
import { Glyph, Icon, type GlyphName } from './glyphs'
import { R5Link, type R5LinkProps } from './R5Link'

/* ------------------------------------------------------------------ PressCard ------------------------------------------------------------------ */

export interface PressCardProps extends Omit<R5LinkProps, 'className'> {
  className?: string
  children: ReactNode
}

/**
 * A card that is one big link or starts something (test cards, quick practice, a recent result): white, 1px line and the
 * ghost press edge. Hover lifts the face 2px and deepens the edge; pressing collapses it (APP-DESIGN-DIRECTION 3.4).
 */
export function PressCard({ className, children, ...rest }: PressCardProps) {
  return <R5Link className={clsx('r5-presscard r5-press', className)} {...rest}>{children}</R5Link>
}

/* ------------------------------------------------------------------ chips and badges ------------------------------------------------------------------ */

/** Where a number came from: "Marked by AI", "Reviewed by a qualified teacher", "Sample data". 12px, paper fill, ink 2. */
export function SourceChip({ children, glyph, className }: { children: ReactNode; glyph?: GlyphName; className?: string }) {
  return (
    <span className={clsx('r5-source', className)}>
      {glyph && <Glyph name={glyph} size={14} />}
      <span>{children}</span>
    </span>
  )
}

/** A small count (nav items, tabs). The digits roll when the number changes while on screen. */
export function CountBadge({ value, className, label }: { value: number; className?: string; label?: string }) {
  return (
    <span className={clsx('r5-countbadge', className)} aria-label={label}>
      <RollingNumber value={String(value)} />
    </span>
  )
}

/** A keyboard hint next to a staff action ("⌘K", "J"). */
export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return <kbd className={clsx('r5-kbd', className)} dir="ltr">{children}</kbd>
}

/** Initials on tint-navy (navy text); `self` is the current user's own avatar, navy with white initials. */
export function Avatar({ initials, size = 32, self = false, className }: { initials: string; size?: 24 | 32 | 40 | 56; self?: boolean; className?: string }) {
  return (
    <span className={clsx('r5-avatar-dot', self && 'r5-avatar-dot--self', className)} style={{ ['--r5-av' as string]: `${size}px` }} aria-hidden="true">
      <bdi data-content="">{initials}</bdi>
    </span>
  )
}

/** A small win (3.10.2): "Up 0.5" with an up chevron that rises and pops. `play` decides whether it animates. */
export function UpChip({ children, play = false, delay = 0, className }: { children: ReactNode; play?: boolean; delay?: number; className?: string }) {
  return (
    <span className={clsx('r5-upchip', className)} data-play={play ? 'true' : 'false'} style={{ ['--r5-delay' as string]: `${delay}ms` }}>
      <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M2.5 7.6 6 4.2l3.5 3.4" /></svg>
      <span>{children}</span>
    </span>
  )
}

/* ------------------------------------------------------------------ choice chips ------------------------------------------------------------------ */

export interface ChoiceOption {
  value: string
  label: string
  disabled?: boolean
}

export interface ChoiceChipsProps {
  options: ChoiceOption[]
  value: string | null
  onChange: (value: string) => void
  /** The group's accessible name, already translated. */
  label: string
  /** Numbers (band chips) keep Inter and an LTR isolate. */
  numeric?: boolean
  className?: string
}

/**
 * Six or fewer options as chips (replacing a default select, 2.6): a `radiogroup` with roving focus (arrow keys move and select,
 * swapped in Arabic). The selected chip fills navy and its check pops in.
 */
export function ChoiceChips({ options, value, onChange, label, numeric = false, className }: ChoiceChipsProps) {
  useLanguage()
  const ref = useRef<HTMLDivElement>(null)
  const id = useId()
  const enabled = options.filter((option) => !option.disabled)
  const index = Math.max(0, enabled.findIndex((option) => option.value === value))
  const move = (step: number) => {
    const next = enabled[(index + step + enabled.length) % enabled.length]
    if (!next) return
    onChange(next.value)
    Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('[data-value]') ?? []).find((chip) => chip.dataset.value === next.value)?.focus()
  }
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const rtl = currentLanguage() === 'ar'
    let step = 0
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') step = 1
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') step = -1
    if ((event.key === 'ArrowRight' || event.key === 'ArrowLeft') && rtl) step = -step
    if (step) { event.preventDefault(); move(step) }
  }
  return (
    <div ref={ref} role="radiogroup" aria-label={label} className={clsx('r5-choices', className)} onKeyDown={onKeyDown}>
      {options.map((option) => {
        const selected = option.value === value
        const focusable = selected || (value === null && option === enabled[0])
        return (
          <button
            key={option.value}
            id={`${id}-${option.value}`}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={option.disabled}
            tabIndex={focusable ? 0 : -1}
            data-value={option.value}
            className={clsx('r5-choice r5-press', selected && 'r5-choice--on')}
            onClick={() => onChange(option.value)}
          >
            {selected && <span className="r5-choice__check" aria-hidden="true"><Icon name="check" size={14} weight={3} /></span>}
            {numeric ? <bdi className="num">{option.label}</bdi> : option.label}
          </button>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------------ icon button ------------------------------------------------------------------ */

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** The accessible name (and the desktop tooltip). Required: an icon button never has a visible label. */
  label: string
  children: ReactNode
  tone?: 'quiet' | 'ghost' | 'navy'
}

/** 40px (student) / 32px (staff) circle. Quiet by default; `ghost` where it needs an edge on a light surface. */
export function IconButton({ label, children, tone = 'quiet', className, type, ...rest }: IconButtonProps) {
  return (
    <button type={type ?? 'button'} className={clsx('r5-iconbtn r5-tap', `r5-iconbtn--${tone}`, className)} aria-label={label} title={label} {...rest}>
      {children}
    </button>
  )
}
